import React, { useCallback, useMemo } from 'react';
import { useFilterBarParams } from 'src/common/components/filter-bar/use-params';
import { generateColumn } from 'src/common/components/table/LegacyTable';
import { Journey } from 'src/common/interfaces/journey.interface';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { Pagination } from 'src/common/Pagination';
import {
  ChevronUpStrongIcon,
  DesktopStrongIcon,
  MobileStrongIcon,
  TabletStrongIcon,
  UnknownStrongIcon,
} from 'src/monet/icons';
import { formatDuration } from 'src/utils/date';
import { DataTable } from 'src/common/components/table/data-table';
import { TooltipComponent } from 'src/common/tooltip/component';
import { useJourneyInsights } from 'src/dashboard/insights/journeys/use-journey-insights.hook';
import { VisitorView } from 'src/editor/insights/constants';
import { getDeviceTypeTooltip } from 'src/editor/insights/visitor-card';
import classNames from 'classnames';
import { DashboardInsightsJourneysJourneyVisitorStepsTable } from './steps-table';
import { Node } from 'src/common/interfaces/node.interface';
import { TableHeaderWithTooltip } from '../../table-header-with-tooltip';
import { DeviceType } from '../../helpers';
import { CSVDownload } from './csv-download';

type Props = {
  journey: Journey;
  contentList: Node[];
  sessionUuid: string;
};

export const DashboardInsightsJourneysJourneyVisitorVisitorsTable = ({ journey, contentList, sessionUuid }: Props) => {
  const {
    filterBarParams: { search_term, page, filters },
    updatePage,
  } = useFilterBarParams();

  // We use useMemo here so that we pass the same object to the api hook below.
  const insightsFilters = useMemo(
    () => ({
      email: search_term,
      node_ids: filters?.step?.map((nodeId) => parseInt(nodeId)),
      alias_ids: filters?.link?.map((aliasId) => parseInt(aliasId)),
      session_uuid: sessionUuid,
    }),
    [search_term, filters]
  );
  const { fetching, pages, showPagination, visitorViews } = useJourneyInsights({
    journeyUuid: journey.uuid,
    page,
    filters: insightsFilters,
  });

  const renderDeviceIcon = (deviceType?: DeviceType) => {
    if (!deviceType) {
      return <UnknownStrongIcon />;
    }

    if (deviceType.form === 'mobile') {
      return <MobileStrongIcon />;
    } else if (deviceType.form === 'desktop') {
      return <DesktopStrongIcon />;
    } else if (deviceType.form === 'tablet') {
      return <TabletStrongIcon />;
    }

    return <UnknownStrongIcon />;
  };

  const columns = useMemo(
    () => [
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Date of Journey open'>Viewed</TableHeaderWithTooltip>,
        accessor: 'viewed',
        width: 160,
        Cell: ({ row }: { row: any }) => row.original.created_at,
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Time spent viewing Journey'>Time spent</TableHeaderWithTooltip>,
        accessor: 'time',
        width: 110,
        Cell: ({ row }: { row: any }) => {
          const visitorView = row.original as VisitorView;
          const { duration } = visitorView;
          return duration ? formatDuration(duration) : '-';
        },
      }),
      generateColumn({
        Header: (
          <TableHeaderWithTooltip tooltip='Total steps viewed in this session'>Completion</TableHeaderWithTooltip>
        ),
        accessor: 'completion',
        width: 100,
        Cell: ({ row }: { row: any }) => `${Math.round(row.original.completed_percentage)}%`,
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Device type during this session'>Device</TableHeaderWithTooltip>,
        accessor: 'device',
        width: 88,
        Cell: ({ row }: { row: any }) => {
          const visitorView = row.original as VisitorView;
          const { device_type } = visitorView;
          return (
            <TooltipComponent
              placement='top'
              tooltipContent={getDeviceTypeTooltip(device_type)}
              theme='journey'
              styling='tooltip'
            >
              {renderDeviceIcon(device_type)}
            </TooltipComponent>
          );
        },
      }),
      generateColumn({
        Header: '',
        accessor: 'actions',
        width: '100%',
        allowShrinking: true,
        Cell: ({ row }: { row: any }) => {
          const visitorView = row.original as VisitorView;
          if (visitorView.duration === 0) {
            return null;
          }
          return (
            <div
              className='mr-2 flex justify-end pointer-events-none items-center'
              {...row.getToggleRowExpandedProps()}
            >
              <div className='w-6 h-6 flex items-center justify-center'>
                <ChevronUpStrongIcon
                  className={classNames('transition-transform', {
                    '-rotate-180': !row.isExpanded,
                  })}
                />
              </div>
            </div>
          );
        },
      }),
    ],
    [journey]
  );

  const onPageChange = useCallback(
    (e: any, newPage: number) => {
      updatePage(newPage);
    },
    [updatePage]
  );

  const onRowClicked = (row: any) => {
    row.toggleRowExpanded();
  };

  const renderRowSubComponent = useCallback(
    ({ row }) => {
      const visitorView = row.original as VisitorView;
      return (
        <DashboardInsightsJourneysJourneyVisitorStepsTable
          journey={journey}
          contentList={contentList}
          sessionUuid={visitorView.session_uuid}
          disableSessionGrouping
        />
      );
    },
    [journey, sessionUuid]
  );

  const renderContent = () => {
    if (fetching) {
      return <FixedLoadingSpinner />;
    }
    if (visitorViews.length === 0) {
      return <div className='text-bedrock-p'>No visitors found</div>;
    }

    return (
      <>
        {visitorViews.length > 0 ? <CSVDownload data={visitorViews} /> : null}
        <DataTable
          columns={columns}
          tableClassName='w-full'
          data={visitorViews}
          onRowClick={onRowClicked}
          renderRowSubComponent={renderRowSubComponent}
        />
      </>
    );
  };

  return (
    <div className='mb-6 w-full'>
      {renderContent()}
      {showPagination && pages > 1 && (
        <div className='fixed bottom-0 left-0 w-[760px] bg-white p-4'>
          <Pagination count={pages} page={page} onChange={onPageChange} />
        </div>
      )}
    </div>
  );
};
